/**
 * Kart temsili.
 *
 * Bu oyunda RENGİN OYUN İÇİNDE HİÇBİR ÖNEMİ YOK — kartın kimliği sadece rütbesi.
 * Renk yalnızca (a) görselde ve (b) ilk elin başlayanını belirleyen "karo 2" için var.
 *
 * Rütbe sıralaması: 3 < 4 < ... < 10 < J < Q < K < A < 2
 * Sayısal karşılığı: 3..14 doğal, "2" = 15 (en yüksek).
 */

export type Suit = 'D' | 'C' | 'H' | 'S'; // karo, sinek, kupa, maça
export const SUITS: readonly Suit[] = ['D', 'C', 'H', 'S'];

export type Rank = 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10 | 11 | 12 | 13 | 14 | 15;
export const RANKS: readonly Rank[] = [3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15];

/** "2" — oyunun en yüksek kartı, üstüne kart atılamaz, atıldığı anda turu keser. */
export const TWO: Rank = 15;

/** 0..51 arası kompakt kart kimliği. rank = 3 + (id >> 2), suit = SUITS[id & 3] */
export type CardId = number;

export const rankOf = (c: CardId): Rank => (3 + (c >> 2)) as Rank;
export const suitOf = (c: CardId): Suit => SUITS[c & 3]!;
export const cardOf = (rank: Rank, suit: Suit): CardId =>
  ((rank - 3) << 2) | SUITS.indexOf(suit);

/** İlk elde oyunu başlatan kartı belirler (yalnızca belirleyici — oynama zorunluluğu yok). */
export const KARO_TWO: CardId = cardOf(TWO, 'D');

const RANK_LABELS: Record<number, string> = {
  10: '10', 11: 'J', 12: 'Q', 13: 'K', 14: 'A', 15: '2',
};
const LABEL_RANKS: Record<string, Rank> = {
  '3': 3, '4': 4, '5': 5, '6': 6, '7': 7, '8': 8, '9': 9,
  '10': 10, J: 11, Q: 12, K: 13, A: 14, '2': 15,
};

export const rankLabel = (r: Rank): string => RANK_LABELS[r] ?? String(r);
export const cardToString = (c: CardId): string => rankLabel(rankOf(c)) + suitOf(c);
export const cardsToString = (cs: readonly CardId[]): string =>
  cs.map(cardToString).join(' ');

/** "KD", "10H", "2D" gibi kısa kodu CardId'ye çevirir. */
export function cardFromString(s: string): CardId {
  const t = s.trim().toUpperCase();
  const suit = t.slice(-1) as Suit;
  const rank = LABEL_RANKS[t.slice(0, -1)];
  if (rank === undefined || !SUITS.includes(suit)) {
    throw new Error(`Geçersiz kart kodu: ${s}`);
  }
  return cardOf(rank, suit);
}

/** "KD KH 2D 3C" → CardId[] */
export const parseCards = (s: string): CardId[] =>
  s.split(/\s+/).filter(Boolean).map(cardFromString);

export const fullDeck = (): CardId[] => Array.from({ length: 52 }, (_, i) => i);

/** Kullanıcı elini rütbeye göre sıralı görüyor (renge göre değil) — kural gereği. */
export const sortHand = (hand: readonly CardId[]): CardId[] =>
  [...hand].sort((a, b) => rankOf(a) - rankOf(b) || (a & 3) - (b & 3));

/** Elin en yüksek n kartı — el sonu değişiminde kaybedenin zorunlu verdiği kartlar. */
export const highestCards = (hand: readonly CardId[], n: number): CardId[] =>
  sortHand(hand).slice(-n);

/** Elin en düşük n kartı — bot/timeout durumunda serbest verilen kartlar için. */
export const lowestCards = (hand: readonly CardId[], n: number): CardId[] =>
  sortHand(hand).slice(0, n);

/** Rütbeye göre gruplar: Map<Rank, CardId[]> (kart listeleri sıralı). */
export function groupByRank(hand: readonly CardId[]): Map<Rank, CardId[]> {
  const g = new Map<Rank, CardId[]>();
  for (const c of sortHand(hand)) {
    const r = rankOf(c);
    const list = g.get(r);
    if (list) list.push(c);
    else g.set(r, [c]);
  }
  return g;
}

/* ------------------------------------------------------------------ *
 * Deterministik karıştırma
 *
 * Dağıtım seed'den üretilir; seed sunucuda saklanır. Bu sayede bir el
 * birebir yeniden oynatılabilir (replay, hile denetimi, bug tekrarı,
 * bot testlerinde tekrarlanabilirlik).
 * ------------------------------------------------------------------ */

export function makeRng(seed: string): () => number {
  let h = 1779033703 ^ seed.length;
  for (let i = 0; i < seed.length; i++) {
    h = Math.imul(h ^ seed.charCodeAt(i), 3432918353);
    h = (h << 13) | (h >>> 19);
  }
  let a = h >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

/** 4 oyuncuya 13'er kart. Koltuk sırasına göre dizi döner. */
export function dealHands(seed: string): CardId[][] {
  const rng = makeRng(seed);
  const deck = fullDeck();
  for (let i = deck.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [deck[i], deck[j]] = [deck[j]!, deck[i]!];
  }
  return [0, 1, 2, 3].map((s) => sortHand(deck.slice(s * 13, s * 13 + 13)));
}
